import { useId, useState } from 'react';
import { ChevronDownIcon, LockIcon } from '@/components/icons';
import { Checkbox } from '@/components/ui/Checkbox';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { Tabs } from '@/components/ui/Tabs';
import {
  administrationResources,
  visibilityOptions,
  type Grant,
  type Overview,
  type Visibility,
} from './access';
import { modulePermissions } from './permission-definitions';
import './RolePermissionsEditor.css';

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');
const scopeDescriptions: Record<Visibility, string> = {
  NONE: 'Ce module est inaccessible. Choisissez une visibilité pour autoriser des actions.',
  OWN: 'Uniquement les fiches dont le collaborateur est responsable.',
  TEAM: 'Ses fiches et celles des membres de ses équipes.',
  ALL: 'Toutes les fiches du module, y compris celles sans responsable.',
};
export const RolePermissionsEditor = ({
  resources,
  permissions,
  disabled,
  onChange,
}: {
  resources: Overview['resources'];
  permissions: Grant[];
  disabled: boolean;
  onChange: (resource: string, patch: Partial<Grant>) => void;
}) => {
  const id = useId();
  const [tab, setTab] = useState<'business' | 'administration'>('business');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const inCategory = resources.filter(
    (resource) =>
      administrationResources.some((key) => key === resource.key) === (tab === 'administration'),
  );
  const term = normalize(search.trim());
  const modules = inCategory.filter((resource) =>
    normalize(
      resource.label +
        ' ' +
        modulePermissions(resource.key)
          .map((action) => action.label + ' ' + action.description + ' ' + action.group)
          .join(' '),
    ).includes(term),
  );
  const enabledCount = permissions.filter((grant) => grant.visibility !== 'NONE').length;
  return (
    <section className="role-permissions" aria-label="Permissions du rôle">
      <div className="role-permissions-heading">
        <div>
          <h2>Permissions du rôle</h2>
          <p>Ouvrez un module pour définir sa visibilité et les actions autorisées.</p>
        </div>
        <span className="access-pill">
          {enabledCount} / {resources.length} modules accessibles
        </span>
      </div>
      <Tabs
        id={id + '-categories'}
        label="Catégories de permissions"
        value={tab}
        onChange={(value) => {
          setTab(value);
          setSearch('');
        }}
        tabs={[
          { value: 'business', label: 'Modules métier' },
          { value: 'administration', label: 'Administration et accès' },
        ]}
        className="w-full bg-white/90 p-1.5"
      />
      <SearchInput
        value={search}
        onValueChange={setSearch}
        placeholder="Rechercher un module ou une permission…"
        aria-label="Rechercher un module ou une permission"
      />
      <div
        role="tabpanel"
        id={id + '-categories-' + tab + '-panel'}
        aria-labelledby={id + '-categories-' + tab}
        className="role-permissions-modules"
      >
        {!modules.length && (
          <p className="role-permissions-empty" role="status">
            Aucun module ne correspond à cette recherche.
          </p>
        )}
        {modules.map((resource) => {
          const grant = permissions.find((item) => item.resource === resource.key)!;
          const actions = modulePermissions(resource.key);
          const activeCount =
            grant.visibility === 'NONE' ? 0 : actions.filter((action) => grant[action.key]).length;
          const global = ['dashboard', 'reference', ...administrationResources].includes(
            resource.key,
          );
          const open = !!term || (expanded[resource.key] ?? resource.key === inCategory[0]?.key);
          const panelId = id + '-module-' + resource.key;
          const groups = [...new Set(actions.map((action) => action.group))];
          return (
            <article
              key={resource.key}
              className="role-permission-module"
              data-enabled={grant.visibility !== 'NONE'}
            >
              <h3>
                <button
                  type="button"
                  className="role-permission-trigger"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => {
                    if (term) setSearch('');
                    setExpanded((current) => ({ ...current, [resource.key]: !open }));
                  }}
                >
                  <span className="role-permission-icon" aria-hidden>
                    <LockIcon />
                  </span>
                  <span className="role-permission-title">
                    <strong>{resource.label}</strong>
                    <small>
                      {grant.visibility === 'NONE'
                        ? 'Aucun accès'
                        : actions.length
                          ? activeCount + ' / ' + actions.length + ' actions autorisées'
                          : 'Consultation du module'}
                    </small>
                  </span>
                  <span className="role-permission-scope">
                    {visibilityOptions.find((option) => option.value === grant.visibility)?.label}
                  </span>
                  <ChevronDownIcon className="role-permission-chevron" />
                </button>
              </h3>
              <div id={panelId} hidden={!open} className="role-permission-content">
                <div className="role-permission-visibility">
                  <Select
                    label={'Visibilité : ' + resource.label}
                    value={grant.visibility}
                    options={
                      global
                        ? visibilityOptions.filter((option) =>
                            ['ALL', 'NONE'].includes(option.value),
                          )
                        : visibilityOptions
                    }
                    disabled={disabled}
                    onChange={(event) =>
                      onChange(resource.key, { visibility: event.target.value as Visibility })
                    }
                  />
                  <p>
                    {global
                      ? grant.visibility === 'NONE'
                        ? 'Ce module est inaccessible.'
                        : 'Accès à ce module global.'
                      : scopeDescriptions[grant.visibility]}
                  </p>
                </div>
                {resource.key === 'actions' && (
                  <p className="access-hint">L’auteur de l’action est utilisé comme responsable.</p>
                )}
                {['timesheets', 'submitted-timesheets'].includes(resource.key) && (
                  <p className="access-hint">La visibilité dépend du responsable de la mission.</p>
                )}
                {groups.map((group) => (
                  <fieldset key={group} className="role-permission-group">
                    <legend>{group}</legend>
                    <div className="role-permission-options">
                      {actions
                        .filter((action) => action.group === group)
                        .map((action) => (
                          <div
                            className="role-permission-option"
                            data-checked={grant.visibility !== 'NONE' && grant[action.key]}
                            key={action.key}
                          >
                            <Checkbox
                              label={action.label}
                              aria-label={action.accessibleLabel + ' : ' + resource.label}
                              aria-describedby={panelId + '-' + action.key + '-description'}
                              checked={grant.visibility !== 'NONE' && grant[action.key]}
                              disabled={disabled || grant.visibility === 'NONE'}
                              onChange={(event) =>
                                onChange(resource.key, { [action.key]: event.target.checked })
                              }
                            />
                            <p id={panelId + '-' + action.key + '-description'}>
                              {action.description}
                            </p>
                          </div>
                        ))}
                    </div>
                  </fieldset>
                ))}
                {!actions.length && (
                  <p className="access-hint">
                    La visibilité suffit pour autoriser la consultation. Les données affichées
                    respectent les droits des modules associés.
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
